// StandaloneSignup.jsx - Completely isolated signup component
import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles, Shield, Zap, ArrowLeft } from 'lucide-react';

const StandaloneSignup = ({ onComplete = () => {}, onSkip = () => {} }) => {
  const [signupType, setSignupType] = useState(null); // 'email' or 'username'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      if (prev[field] === value) return prev;
      return {
        ...prev,
        [field]: value,
      };
    });
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setErrors({});

    try {
      if (signupType === 'email') {
        if (!formData.email || !formData.password) {
          setErrors({ general: 'Please fill in all fields' });
          return;
        }
        
        if (formData.password !== formData.confirmPassword) {
          setErrors({ confirmPassword: 'Passwords do not match' });
          return;
        }

        // Simulate account creation
        console.log('Creating email account:', { email: formData.email, password: formData.password });
        
        // Save to localStorage for demo
        localStorage.setItem('vibe_user', JSON.stringify({
          type: 'email',
          email: formData.email,
          username: formData.email.split('@')[0],
          createdAt: new Date().toISOString(),
        }));
        
      } else {
        if (!formData.username || !formData.password) {
          setErrors({ general: 'Please fill in all fields' });
          return;
        }
        
        if (formData.password !== formData.confirmPassword) {
          setErrors({ confirmPassword: 'Passwords do not match' });
          return;
        }

        // Simulate account creation
        console.log('Creating username account:', { username: formData.username, password: formData.password });
        
        // Save to localStorage for demo
        localStorage.setItem('vibe_user', JSON.stringify({
          type: 'username',
          username: formData.username,
          createdAt: new Date().toISOString(),
        }));
      }
      
      // Small delay to simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onComplete();
    } catch (error) {
      setErrors({ general: error.message || 'Signup failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const SignupTypeSelection = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center"
        >
          <Shield className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-2xl font-bold text-white mb-2">Save Your Progress</h2>
        <p className="text-slate-300">Choose how you&apos;d like to create your account</p>
      </div>

      <div className="space-y-4">
        <motion.button
          onClick={() => setSignupType('email')}
          className="w-full p-6 bg-slate-800/50 border border-slate-700 rounded-xl text-left hover:bg-slate-800/70 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-4">
            <Mail className="w-6 h-6 text-purple-400" />
            <div>
              <h3 className="font-semibold text-white mb-1">Email Signup</h3>
              <p className="text-sm text-slate-400">Full account with email verification</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          onClick={() => setSignupType('username')}
          className="w-full p-6 bg-slate-800/50 border border-slate-700 rounded-xl text-left hover:bg-slate-800/70 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-4">
            <User className="w-6 h-6 text-purple-400" />
            <div>
              <h3 className="font-semibold text-white mb-1">Username Only</h3>
              <p className="text-sm text-slate-400">Quick setup without email (perfect for younger users)</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          onClick={onSkip}
          className="w-full p-4 text-slate-400 hover:text-white transition-colors text-center"
          whileHover={{ scale: 1.02 }}
        >
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>Continue as Guest (progress won&apos;t be saved)</span>
          </div>
        </motion.button>
      </div>
    </div>
  );

  const SignupForm = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <button
          onClick={() => setSignupType(null)}
          className="text-slate-400 hover:text-white mb-4"
        >
          ← Back to options
        </button>
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center"
        >
          <Zap className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {signupType === 'email' ? 'Email Signup' : 'Username Signup'}
        </h2>
        <p className="text-slate-300">
          {signupType === 'email' 
            ? 'Create your account with email' 
            : 'Create your account with just a username'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {signupType === 'email' ? (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInputChange('email', e.target.value);
                }}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="your@email.com"
                required
              />
            </div>
            {errors.email && (
              <p className="text-red-400 text-sm mt-1">{errors.email}</p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                autoComplete="username"
                value={formData.username}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInputChange('username', e.target.value);
                }}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Choose a username"
                required
              />
            </div>
            {errors.username && (
              <p className="text-red-400 text-sm mt-1">{errors.username}</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleInputChange('password', e.target.value);
              }}
              className="w-full pl-10 pr-12 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Create a password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-400 text-sm mt-1">{errors.password}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleInputChange('confirmPassword', e.target.value);
              }}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Confirm your password"
              required
            />
          </div>
          {errors.confirmPassword && (
            <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        {errors.general && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{errors.general}</p>
          </div>
        )}

        <motion.button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <span>Create Account</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </motion.button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
      {/* Simple back button */}
      <div className="p-4">
        <button
          onClick={() => {
            // Navigate back to home by removing the signup parameter
            window.location.href = window.location.origin;
          }}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {!signupType ? <SignupTypeSelection /> : <SignupForm />}
        </div>
      </div>
    </div>
  );
};

export default StandaloneSignup;